import React, { useEffect, useState } from 'react';
import { Package } from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';

export const hasKit = (p) => !!p && ((p.box_items || []).length > 0 || (p.option_groups || []).length > 0);

// Confirms a product's box list + option choices; returns [{ item, quantity }] via onConfirm.
export const ProductKitDialog = ({ product, items, unitQty = 1, onConfirm, onClose }) => {
  const [boxOn, setBoxOn] = useState({});
  const [picks, setPicks] = useState({});
  const bySku = Object.fromEntries(items.map(it => [it.item_sku, it]));

  useEffect(() => {
    if (!product) return;
    setBoxOn(Object.fromEntries((product.box_items || []).map((_, i) => [i, true])));
    setPicks({});
  }, [product]);

  if (!product) return null;
  const groups = product.option_groups || [];
  const missing = groups.some((g, gi) => g.required !== false && picks[gi] == null);
  const nameOf = (sku) => (bySku[sku] ? `${sku} — ${bySku[sku].item_name}` : `${sku} (not in Item Master)`);
  const price = (sku) => (bySku[sku]?.sales_price != null ? ` · $${Number(bySku[sku].sales_price).toFixed(2)}` : '');

  const confirm = () => {
    const out = [];
    (product.box_items || []).forEach((k, i) => { if (boxOn[i]) out.push({ sku: k.item_sku, quantity: Number(k.quantity || 1) * unitQty, bundled: !!product.bundle_pricing }); });
    groups.forEach((g, gi) => { const c = (g.choices || [])[picks[gi]]; if (c) out.push({ sku: c.item_sku, quantity: Number(c.quantity || 1) * unitQty, group: g.name }); });
    onConfirm(out.map(o => ({ ...o, item: bySku[o.sku] || null })));
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="product-kit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Package className="w-5 h-5 text-orange-600" />{product.product_type_name}: box contents</DialogTitle>
          <DialogDescription>These lines will be added below the product. Untick anything not shipping.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[55vh] overflow-y-auto py-1">
          {(product.box_items || []).length > 0 && (
            <div className="space-y-1.5">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Included</div>
              {product.box_items.map((k, i) => (
                <label key={i} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`kit-box-${i}`}>
                  <Checkbox checked={!!boxOn[i]} onCheckedChange={(v) => setBoxOn({ ...boxOn, [i]: !!v })} data-testid={`kit-box-check-${i}`} />
                  <span className="mono text-xs text-slate-500 w-10 text-right">{Number(k.quantity) * unitQty}×</span>
                  <span className="truncate">{nameOf(k.item_sku)}<span className="text-slate-400">{product.bundle_pricing ? ' · $0 (included)' : price(k.item_sku)}</span></span>
                </label>
              ))}
            </div>
          )}
          {groups.map((g, gi) => (
            <div key={gi} className="space-y-1.5" data-testid={`kit-group-${gi}`}>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{g.name}{g.required !== false ? ' *' : ' (optional)'}</div>
              <RadioGroup value={picks[gi] != null ? String(picks[gi]) : ''} onValueChange={(v) => setPicks({ ...picks, [gi]: Number(v) })}>
                {(g.choices || []).map((c, ci) => (
                  <label key={ci} className="flex items-center gap-2 text-sm cursor-pointer">
                    <RadioGroupItem value={String(ci)} data-testid={`kit-group-${gi}-choice-${ci}`} />
                    <span className="mono text-xs text-slate-500 w-10 text-right">{Number(c.quantity) * unitQty}×</span>
                    <span className="truncate">{nameOf(c.item_sku)}<span className="text-slate-400">{price(c.item_sku)}</span></span>
                  </label>
                ))}
              </RadioGroup>
              {g.required === false && picks[gi] != null && <button type="button" className="text-[11px] text-slate-500 underline" onClick={() => { const n = { ...picks }; delete n[gi]; setPicks(n); }}>clear choice</button>}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="kit-skip-btn">Skip accessories</Button>
          <Button onClick={confirm} disabled={missing} className="bg-orange-500 hover:bg-orange-600" data-testid="kit-confirm-btn">{missing ? 'Pick each required option' : 'Add lines'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
